import { useEffect } from 'react';

export const SITE = 'https://visiamedicalgroup.com';
export const BRAND = 'Visia Medical Group';

/**
 * Заголовок, опис і schema.org для конкретної сторінки.
 *
 * Бібліотеку на кшталт react-helmet не беремо навмисно: пререндер знімає
 * document.documentElement.outerHTML уже після того, як відпрацювали ефекти,
 * тож достатньо просто правити <head> напряму — і в статичному HTML, який
 * бачить Google, усе вже стоїть на місці.
 *
 * Кожен тег саме ПЕРЕПИСУЄТЬСЯ, а не додається: у public/index.html лежать
 * значення для головної, і без цього на внутрішніх сторінках виходило б по
 * два <title> і два canonical.
 *
 * Назву бренду в кінець заголовка дописує лише цей компонент. Коли кожна
 * сторінка писала її сама, у видачі стояли вперемішку «| Visia Medical Group»
 * і «| VMG». Сторінка передає тільки власну частину; якщо бренд у ній уже є
 * (головна з нього починається), нічого не дописуємо — інакше він стояв би
 * двічі. Разом із суфіксом заголовок має вкладатися в 60 знаків: довший
 * Google обріже, і відріже якраз бренд.
 */
const withBrand = (title) => (title.includes(BRAND) ? title : `${title} | ${BRAND}`);

/**
 * Тип сторінки для соцмереж виводимо зі схеми, а не окремим пропом: сторінка,
 * яка описує себе в schema.org як статтю, і для Facebook має бути статтею, а
 * два незалежні прапорці рано чи пізно розійшлися б.
 */
const ARTICLE_TYPES = ['Article', 'BlogPosting', 'NewsArticle'];
const findArticle = (schema) => {
  if (!schema) return null;
  const nodes = schema['@graph'] || [schema];
  return nodes.find((node) => ARTICLE_TYPES.includes(node['@type'])) || null;
};

const upsert = (selector, create, apply) => {
  let el = document.head.querySelector(selector);
  if (!el) {
    el = create();
    document.head.appendChild(el);
  }
  apply(el);
};

const meta = (attr, name, content) => {
  if (!content) return;
  upsert(`meta[${attr}="${name}"]`, () => {
    const el = document.createElement('meta');
    el.setAttribute(attr, name);
    return el;
  }, (el) => el.setAttribute('content', content));
};

const Seo = ({ title, description, path, schema, noindex }) => {
  useEffect(() => {
    const url = SITE + path;
    const fullTitle = withBrand(title);
    const article = findArticle(schema);

    document.title = fullTitle;
    meta('name', 'description', description);
    meta('name', 'robots', noindex ? 'noindex, follow' : 'index, follow, max-image-preview:large');

    upsert('link[rel="canonical"]', () => {
      const el = document.createElement('link');
      el.rel = 'canonical';
      return el;
    }, (el) => el.setAttribute('href', url));

    meta('property', 'og:title', fullTitle);
    meta('property', 'og:description', description);
    meta('property', 'og:url', url);
    meta('name', 'twitter:title', fullTitle);
    meta('name', 'twitter:description', description);

    // Тип скидаємо на кожній сторінці: після переходу зі статті на послугу
    // без перезавантаження інакше лишився б «article» з датами статті.
    meta('property', 'og:type', article ? 'article' : 'website');
    document.head.querySelectorAll('meta[property^="article:"]').forEach((el) => el.remove());
    if (article) {
      meta('property', 'article:published_time', article.datePublished);
      meta('property', 'article:modified_time', article.dateModified);
    }

    // Схема сторінки живе окремим тегом із власним id: базовий @graph про
    // компанію лишається в index.html недоторканим на всіх сторінках.
    const previous = document.getElementById('page-schema');
    if (previous) previous.remove();
    if (schema) {
      const el = document.createElement('script');
      el.type = 'application/ld+json';
      el.id = 'page-schema';
      el.textContent = JSON.stringify(schema);
      document.head.appendChild(el);
    }
  }, [title, description, path, schema, noindex]);

  return null;
};

export default Seo;
